import { Component, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';
import { CloseIcon, PrevIcon, NextIcon, SearchIcon } from './icons';

@Component({
  tag: 'presto-search-bar-ui',
  styleUrl: 'presto-search-bar-ui.scss',
  shadow: true,
})
export class PrestoSearchBarUi {
  private placeholderElement: HTMLDivElement;
  private input: HTMLInputElement;

  /** The value for the search. */
  @Prop({ mutable: true }) value: string;
  /** The placeholder. */
  @Prop() placeholder: string;
  /** Has results */
  @Prop() hasNavigation: boolean;

  /** Is this focused */
  @State() focused: boolean;
  /** The placeholder width. */
  @State() placeholderWidth: number;

  /** Previous is navigated.*/
  @Event() previousNav: EventEmitter<void>;
  /** Next is navigated */
  @Event() nextNav: EventEmitter<void>;
  /** Search is performed */
  @Event() search: EventEmitter<string>;
  /** Search is performed */
  @Event() focusChange: EventEmitter<boolean>;

  /** Handle the search */
  handleSearch(e) {
    e.preventDefault();
    this.value = e.target.value;
  }

  /** Handle the focus of the input. */
  handleFocus() {
    this.focused = true;
  }

  handleBlur() {
    if (!this.value) {
      this.focused = false;
    }
  }

  componentDidLoad() {
    this.handlePlaceholderSize();
  }

  @Watch('placeholder')
  handlePlaceholderSize() {
    setTimeout(() => {
      this.placeholderWidth = this.placeholderElement.offsetWidth;
    }, 50);
  }

  @Watch('value')
  handleValueChange() {
    this.search.emit(this.value);
  }

  @Watch('focused')
  watchPropHandler(focus: boolean) {
    this.focusChange.emit(focus);
  }

  handleClick() {
    this.focused = true;
    setTimeout(() => {
      this.input.focus();
    }, 50);
  }

  handleClear(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.value = '';
    this.focused = false;
    return false;
  }

  handleNext(e) {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.nextNav.emit();
  }

  handlePrevious(e) {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.previousNav.emit();
  }

  /**
   * Rendering the component
   */
  render() {
    return (
      <div
        class={{
          'search-bar': true,
          'search-bar--is-active': this.focused || !!this.value,
          'search-bar--has-navigation': this.hasNavigation,
        }}
        onClick={() => this.handleClick()}
      >
        <span class="search-bar__search-icon search-bar__icon">
          <SearchIcon />
        </span>

        <div class="search-bar__placeholder" ref={el => (this.placeholderElement = el as HTMLDivElement)} aria-hidden>
          {this.placeholder}
        </div>

        <input
          type="text"
          value={this.value}
          placeholder={this.placeholder}
          ref={el => (this.input = el as HTMLInputElement)}
          style={{ width: (this.placeholderWidth || 0) + 'px' }}
          onInput={e => this.handleSearch(e)}
          onFocus={() => this.handleFocus()}
          onBlur={() => this.handleBlur()}
        />

        <span class="search-bar__controls">
          <span class="search-bar__control-prev" onClick={e => this.handlePrevious(e)}>
            <PrevIcon />
          </span>
          <span class="search-bar__control-next" onClick={e => this.handleNext(e)}>
            <NextIcon />
          </span>
          <div class="search-bar__control-clear" onClick={e => this.handleClear(e)}>
            <CloseIcon />
          </div>
        </span>
      </div>
    );
  }
}
